import { Injectable } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Address, BookableStorageLocation, IGoogleMapsService, Place } from '@luggagehero/shared/interfaces';

const GOOGLE_MAPS_URL = 'https://www.google.com/maps';
@Injectable({
  providedIn: 'root',
})
export class SharedGoogleMapsService implements IGoogleMapsService {
  public linkToPlaces = false;
  public mapStyles = AppConfig.GOOGLE_MAPS_STYLES['DROPNLOCAL'];
  // A getter for the same reason AppConfig.GOOGLE_MAPS_OPTIONS is one: it reads the Maps SDK, and this
  // service is also used where no map is shown (the booking page's directions links), before the SDK loads.
  public get mapOptions(): google.maps.MapOptions {
    return AppConfig.GOOGLE_MAPS_OPTIONS['DEFAULT'];
  }

  // NOTE(AM-4515): client-side geocode()/autocomplete() (which instantiated google.maps.Geocoder /
  // places.AutocompleteService using the exposed browser Maps key) were removed. The dashboard
  // create/edit flows now geocode via the server (SharedGeocodingService.geocodeAddress →
  // dashboard-api GET /places/geocode); autocomplete already ran server-side. The pure parsing
  // helpers below remain (no Maps-key usage), used to shape backend/geocoder results into Place.

  parsePlaceResult(data: google.maps.places.PlaceResult): Place | null {
    if (!data.address_components) {
      return null;
    }
    const location = data.geometry?.location;
    if (!location) {
      return null;
    }
    const address = this.parseAddress(
      data.address_components,
      data.formatted_address ?? '',
      location.lat(),
      location.lng(),
    );
    const place: Place = {
      id: data.place_id ?? '',
      name: data.name ?? '',
      address: address,
    };
    return place;
  }

  parseGeocoderResult(value: google.maps.GeocoderResult): Place | null {
    if (!value.address_components) {
      return null;
    }
    const address = this.parseAddress(
      value.address_components,
      value.formatted_address,
      value.geometry.location.lat(),
      value.geometry.location.lng(),
    );
    const name = value.formatted_address ? value.formatted_address.split(',')[0] : '';
    const place: Place = {
      id: value.place_id,
      name: name,
      address: address,
    };
    return place;
  }

  parseAddress(
    addressComponents: google.maps.GeocoderAddressComponent[],
    formattedAddress: string,
    lat: number,
    lon: number,
  ): Address {
    const address = {} as Address;
    addressComponents.forEach((addressComponent) => {
      switch (addressComponent.types[0]) {
        case 'street_number':
          address.street = addressComponent.long_name;
          break;

        case 'route':
          address.street = address.street
            ? `${address.street} ${addressComponent.long_name}`
            : addressComponent.long_name;
          break;

        case 'locality':
        case 'postal_town':
          address.city = addressComponent.long_name;
          break;

        case 'country':
          address.country = addressComponent.long_name;
          address.countryCode = addressComponent.short_name.toLowerCase();
          address.region = address.countryCode;
          break;

        case 'administrative_area_level_1':
          address.state = addressComponent.short_name;
          address.stateLong = addressComponent.long_name;
          break;

        case 'postal_code':
          address.postal = addressComponent.long_name;
          break;
      }
    });
    address.formattedAddress = formattedAddress || '';
    address.location = [lon, lat];

    return address;
  }

  parseCity(addressComponents: google.maps.GeocoderAddressComponent[]): string {
    const cityComponent = addressComponents.find((comp) => comp.types[0] === 'postal_town');
    return cityComponent ? cityComponent.long_name.toLowerCase() : '';
  }

  parseRegion(addressComponents: google.maps.GeocoderAddressComponent[]): string {
    const countryComponent = addressComponents.find((comp) => comp.types[0] === 'country');
    return countryComponent ? countryComponent.short_name.toLowerCase() : '';
  }

  getDirectionsLink(storageLocation: BookableStorageLocation): string;
  getDirectionsLink(location: google.maps.LatLng): string;
  getDirectionsLink(locationOrStorageLocation: google.maps.LatLng | BookableStorageLocation): string {
    const utmParams = 'utm_source=booking_confirmation&utm_medium=maps_link';

    // Try to parse the location as a google.maps.LatLng first
    const location = locationOrStorageLocation as google.maps.LatLng;
    if (typeof location.lat === 'function' && typeof location.lng === 'function') {
      const lat = location.lat();
      const lng = location.lng();
      return `${GOOGLE_MAPS_URL}/dir/?api=1&destination=${lat},${lng}&${utmParams}`;
    }

    // Fall back to parsing the location as a BookableStorageLocation
    const storageLocation = locationOrStorageLocation as BookableStorageLocation;
    const gbpPlaceId = storageLocation.externalBusinessProfiles?.gbp?.placeId;
    const placeId = storageLocation.place?.id;

    if (gbpPlaceId || placeId) {
      return `${GOOGLE_MAPS_URL}/dir/?api=1&destination=Google&destination_place_id=${gbpPlaceId || placeId}&${utmParams}`;
    }

    const lat = storageLocation.location.coordinates[1];
    const lng = storageLocation.location.coordinates[0];
    return `${GOOGLE_MAPS_URL}/dir/?api=1&destination=${lat},${lng}&${utmParams}`;
  }

  getPlaceLink(storageLocation: BookableStorageLocation): string | null {
    if (!storageLocation) {
      return null;
    }

    const utmParams = 'utm_source=booking_confirmation&utm_medium=maps_link';
    const lat = storageLocation.location.coordinates[1];
    const lng = storageLocation.location.coordinates[0];
    const gbpPlaceId = storageLocation.externalBusinessProfiles?.gbp?.placeId;
    const placeId = storageLocation.place?.id;

    if (gbpPlaceId || placeId) {
      return `${GOOGLE_MAPS_URL}/search/?api=1&query=${lat},${lng}&query_place_id=${gbpPlaceId || placeId}&${utmParams}`;
    }

    // Fall back to just using coordinates
    return `${GOOGLE_MAPS_URL}/place/${lat},${lng}/@${lat},${lng}&${utmParams}`;
  }
}
